/**
 * Opens a native connection on demand. The caller owns the schema and must
 * close the returned database when finished. Upgrade callbacks are synchronous;
 * enqueue schema/data changes using the supplied upgrade transaction.
 */
export function openDatabase({ name, version, upgrade }) {
  return new Promise((resolve, reject) => {
    if (!globalThis.indexedDB) {
      reject(new Error('IndexedDB is unavailable'));
      return;
    }

    const request = globalThis.indexedDB.open(name, version);
    let abandoned = false;
    let upgradeError;

    request.onblocked = () => {
      abandoned = true;
      reject(new DOMException('IndexedDB open is blocked', 'InvalidStateError'));
    };

    request.onupgradeneeded = (event) => {
      // A blocked open cannot be cancelled. Abort any later schema changes.
      if (abandoned) {
        request.transaction.abort();
        return;
      }
      try {
        upgrade?.(request.result, event.oldVersion, event.newVersion, request.transaction);
      } catch (error) {
        upgradeError = error;
        request.transaction.abort();
      }
    };

    request.onerror = () => reject(upgradeError || request.error);

    request.onsuccess = () => {
      const database = request.result;
      if (abandoned) {
        database.close();
        return;
      }

      // Release this connection so another tab can upgrade or delete the DB.
      database.addEventListener('versionchange', () => database.close());
      resolve(database);
    };
  });
}

/** Waits for a newly created request. Success does not imply a committed write. */
export function waitForRequest(request) {
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      request.removeEventListener('success', onSuccess);
      request.removeEventListener('error', onError);
    };

    const onSuccess = () => {
      cleanup();
      resolve(request.result);
    };

    const onError = () => {
      cleanup();
      reject(request.error);
    };

    request.addEventListener('success', onSuccess);
    request.addEventListener('error', onError);
  });
}

/**
 * Register immediately after creating a transaction, before awaiting requests.
 * Only completion confirms a write; a request error can still be cancelled by
 * the caller, so wait for the final abort rather than a bubbling error event.
 */
export function waitForTransaction(transaction) {
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      transaction.removeEventListener('complete', onComplete);
      transaction.removeEventListener('abort', onAbort);
    };

    const onComplete = () => {
      cleanup();
      resolve();
    };

    const onAbort = () => {
      cleanup();
      reject(transaction.error || new DOMException('IndexedDB transaction aborted', 'AbortError'));
    };

    transaction.addEventListener('complete', onComplete);
    transaction.addEventListener('abort', onAbort);
  });
}
